<template>
	<div class="pdd mini-setup" v-loading="pageLoading">
		<div v-if="!configured" class="guide-card">
			<div class="guide-title">开通小程序店铺</div>
			<p class="guide-desc">填写下方 AppID 与店铺名称后保存即可开通。保存后将自动创建小程序店铺。</p>
		</div>

		<div v-else class="status-card" :class="shop ? 'is-ready' : 'is-missing'">
			<div class="status-main">
				<div class="status-label">{{ shop ? '已建店' : '店铺未创建' }}</div>
				<div class="status-name">{{ shop ? ('小程序店铺：' + shopDisplayName) : '保存配置后将自动建店；若仍未创建可点下方按钮补建。' }}</div>
			</div>
			<div class="status-actions">
				<el-button v-if="!shop" type="primary" size="small" :loading="creating" @click="onCreateShop">创建店铺</el-button>
				<template v-else>
					<el-button type="primary" size="small" @click="goShopConfig">去店铺配置</el-button>
					<el-button size="small" @click="onGoods(shop)">商品管理</el-button>
				</template>
			</div>
		</div>

		<div v-if="list.length" class="tablebox">
			<div class="tablediv shop-table-wrap">
				<el-table :data="list" size="small" class="shop-table">
					<el-table-column label="店铺图标" width="76" align="center" header-align="center">
						<template slot-scope="scope">
							<div class="shop-logo">
								<img v-if="scope.row.logo" :src="scope.row.logo" alt="">
								<i v-else class="el-icon-mobile-phone"></i>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="店铺名称" min-width="180" align="center" header-align="center" show-overflow-tooltip>
						<template slot-scope="scope">
							<span class="shop-name">{{ getShopDisplayName(scope.row) }}</span>
						</template>
					</el-table-column>
					<el-table-column label="绑定时间" min-width="160" align="center" header-align="center">
						<template slot-scope="scope">
							{{ formatDate(scope.row.create_time) }}
						</template>
					</el-table-column>
					<el-table-column label="状态" width="128" align="center" header-align="center">
						<template slot-scope="scope">
							<div class="status-cell" :class="scope.row.state === 1 ? 'is-on' : 'is-off'">
								<el-switch
									v-model="scope.row.state"
									:active-value="1"
									:inactive-value="2"
									active-color="#ff4714"
									inactive-color="#dcdfe6"
									@change="onswitch(scope.row)"
								/>
								<span class="status-label">{{ scope.row.state === 1 ? '启用' : '停用' }}</span>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="操作" width="180" align="center" header-align="center">
						<template slot-scope="scope">
							<span class="cell-link" @click="onsave(scope.row)">店铺配置</span>
							<span class="cell-link cell-link-gap" @click="onGoods(scope.row)">商品管理</span>
						</template>
					</el-table-column>
				</el-table>
			</div>
		</div>

		<el-form ref="form" :model="form" :rules="formRules" label-width="130px" size="small" class="setup-form">
			<div class="form-section">
				<div class="section-title">基础设置</div>
				<el-form-item label="店铺名称" prop="shop_name">
					<el-input v-model="form.shop_name" maxlength="64" placeholder="请输入店铺名称" />
				</el-form-item>
				<el-form-item label="店铺 Logo">
					<div class="logo-upload">
						<myupload :uptype="1" :imgurl="form.logo_url" @resultUrl="onLogoUrl" />
					</div>
					<p class="field-tip">非空才会更新，本轮不支持清空</p>
				</el-form-item>
				<el-form-item v-if="configured" label="C 端状态">
					<el-switch
						:value="form.state === 1"
						active-color="#ff4714"
						inactive-color="#dcdfe6"
						:disabled="toggling"
						@change="onToggle"
					/>
					<span class="state-text">{{ form.state === 1 ? '启用' : '停用' }}</span>
					<p class="field-tip">控制小程序 C 端是否可用，与店铺启停相互独立</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">小程序账号</div>
				<el-form-item label="AppID" prop="appid">
					<el-input v-model="form.appid" maxlength="64" placeholder="wx 开头的小程序 AppID" />
				</el-form-item>
				<el-form-item label="AppSecret">
					<el-input v-model="form.appsecret" type="password" show-password :placeholder="secretPlaceholder(hasAppsecret)" autocomplete="new-password" />
					<p class="field-tip">{{ hasAppsecret ? '已配置，不修改请留空' : '用于登录 code2session，可稍后填写' }}</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">微信支付</div>
				<el-form-item label="商户号">
					<el-input v-model="form.mch_id" maxlength="32" :placeholder="hasMchId ? (mchIdDisplay || '不修改请留空') : '微信支付商户号'" />
					<p v-if="hasMchId && mchIdDisplay" class="field-tip">当前：{{ mchIdDisplay }}，不修改请留空</p>
				</el-form-item>
				<el-form-item label="APIv2 密钥">
					<el-input v-model="form.api_key" type="password" show-password :placeholder="secretPlaceholder(hasApiKey)" autocomplete="new-password" />
					<p class="field-tip">{{ hasApiKey ? '已配置，不修改请留空' : '32 位商户密钥，可稍后填写' }}</p>
				</el-form-item>
				<el-form-item v-if="configured && !hasCert" label="支付证书">
					<el-alert title="未上传证书，退款功能暂不可用。本轮不提供上传入口，证书由后端配置。" type="warning" :closable="false" show-icon />
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">定价配置</div>
				<p class="field-tip price-formula">小程序零售价 = (进货价 + 运费) × (1 + 毛利率 + 扣点率)。命中阶梯用该档毛利率（40=40%），未命中用兜底毛利率。保存后约 10 分钟内生效。</p>
				<el-form-item label="兜底毛利率" prop="profit_margin_percent">
					<el-input v-model="form.profit_margin_percent" placeholder="0-100">
						<template slot="append">%</template>
					</el-input>
					<p class="field-tip">阶梯未命中时使用，提交按千分比（20% → 200）</p>
				</el-form-item>
				<el-form-item label="阶梯毛利率">
					<div class="tier-table">
						<div v-for="(row, index) in form.tierList" :key="'tier-' + index" class="tier-row">
							<el-input v-model="row.min_price_yuan" placeholder="最低进货价" size="mini">
								<template slot="append">元</template>
							</el-input>
							<span class="tier-sep">—</span>
							<el-input v-model="row.max_price_yuan" placeholder="最高（空=无上限）" size="mini">
								<template slot="append">元</template>
							</el-input>
							<el-input v-model="row.margin" placeholder="毛利率" size="mini" class="tier-margin">
								<template slot="append">%</template>
							</el-input>
							<el-button type="text" size="mini" @click="removeTier(index)">删除</el-button>
						</div>
						<el-button type="text" size="mini" :disabled="form.tierList.length >= 20" @click="addTier">添加档位</el-button>
					</div>
					<p class="field-tip">最多 20 档；最高价留空表示无上限。传空列表将清空阶梯。</p>
				</el-form-item>
				<el-form-item label="扣点率" prop="deduction_rate_percent">
					<el-input v-model="form.deduction_rate_percent" placeholder="如 0.6">
						<template slot="append">%</template>
					</el-input>
					<p class="field-tip">一位小数百分比，提交 ×10 为千分比（0.6% → 6）</p>
				</el-form-item>
				<el-form-item label="运费" prop="shipping_fee_yuan">
					<el-input v-model="form.shipping_fee_yuan" placeholder="0-1000">
						<template slot="append">元</template>
					</el-input>
					<p class="field-tip">计入定价公式；一期 C 端仍包邮</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">提现</div>
				<el-form-item label="最低提现金额" prop="min_withdraw">
					<el-input v-model="form.min_withdraw" placeholder="单位：元">
						<template slot="append">元</template>
					</el-input>
				</el-form-item>
			</div>

			<el-form-item>
				<el-button type="primary" :loading="saving" @click="onSave">保存</el-button>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
	import {
		SHOP_TYPE_MINIPROGRAM,
		MINIPROGRAM_API,
		MINI_CONFIG_API,
		getShopDisplayName,
		canUseMiniprogram,
		getApiErrorMessage,
		pickMinIdMiniShop,
	} from '@/constants/retailPlatform'

	export default {
		name: 'MiniprogramAccount',
		inject: ['appdata'],
		data() {
			const appidValidator = (rule, value, callback) => {
				const appid = String(value || '').trim()
				if (!appid) {
					callback(new Error('请填写小程序 AppID'))
					return
				}
				if (!/^wx/i.test(appid)) {
					callback(new Error('AppID 需以 wx 开头'))
					return
				}
				callback()
			}
			const profitPercentValidator = (rule, value, callback) => {
				if (value === '' || value == null) {
					callback()
					return
				}
				const num = Number(value)
				if (isNaN(num) || num < 0 || num > 100) {
					callback(new Error('兜底毛利率需在 0%-100%'))
					return
				}
				callback()
			}
			const deductionValidator = (rule, value, callback) => {
				if (value === '' || value == null) {
					callback()
					return
				}
				const num = Number(value)
				if (isNaN(num) || num < 0 || num > 100) {
					callback(new Error('扣点率需在 0%-100%'))
					return
				}
				callback()
			}
			const shippingValidator = (rule, value, callback) => {
				if (value === '' || value == null) {
					callback()
					return
				}
				const num = Number(value)
				if (isNaN(num) || num < 0 || num > 1000) {
					callback(new Error('运费需在 0-1000 元'))
					return
				}
				callback()
			}
			const withdrawValidator = (rule, value, callback) => {
				if (value === '' || value == null) {
					callback()
					return
				}
				const num = Number(value)
				if (isNaN(num) || num <= 0) {
					callback(new Error('最低提现金额需大于 0'))
					return
				}
				callback()
			}
			return {
				pageLoading: false,
				saving: false,
				creating: false,
				toggling: false,
				configured: false,
				shop: null,
				list: [],
				hasAppsecret: false,
				hasMchId: false,
				hasApiKey: false,
				hasCert: false,
				mchIdDisplay: '',
				profitMargin: 200,
				form: {
					appid: '',
					appsecret: '',
					shop_name: '',
					logo_url: '',
					mch_id: '',
					api_key: '',
					min_withdraw: '100',
					state: 1,
					profit_margin_percent: '20',
					deduction_rate_percent: '0',
					shipping_fee_yuan: '0',
					tierList: [],
				},
				formRules: {
					appid: [{ required: true, validator: appidValidator, trigger: 'blur' }],
					shop_name: [{ required: true, message: '请填写店铺名称', trigger: 'blur' }],
					min_withdraw: [{ validator: withdrawValidator, trigger: 'blur' }],
					profit_margin_percent: [{ validator: profitPercentValidator, trigger: 'blur' }],
					deduction_rate_percent: [{ validator: deductionValidator, trigger: 'blur' }],
					shipping_fee_yuan: [{ validator: shippingValidator, trigger: 'blur' }],
				},
			}
		},
		computed: {
			shopDisplayName() {
				return getShopDisplayName(this.shop)
			},
		},
		mounted() {
			this.loadAll()
		},
		methods: {
			getShopDisplayName,
			secretPlaceholder(hasValue) {
				return hasValue ? '不修改请留空' : '请输入'
			},
			onLogoUrl(url) {
				this.form.logo_url = url || ''
			},
			addTier() {
				if (this.form.tierList.length >= 20) {
					this.$message.warning('阶梯毛利率最多 20 档')
					return
				}
				this.form.tierList.push({
					min_price_yuan: '',
					max_price_yuan: '',
					margin: '',
				})
			},
			removeTier(index) {
				this.form.tierList.splice(index, 1)
			},
			mapTiersFromApi(tiers) {
				if (!Array.isArray(tiers) || !tiers.length) {
					return []
				}
				return tiers.map((tier) => {
					const minFen = tier && tier.min_price
					const maxFen = tier && tier.max_price
					return {
						min_price_yuan: minFen == null || minFen === '' ? '' : String(Number(minFen) / 100),
						max_price_yuan: maxFen == null || maxFen === '' ? '' : String(Number(maxFen) / 100),
						margin: tier && tier.margin != null && tier.margin !== '' ? String(tier.margin) : '',
					}
				})
			},
			buildTiersPayload() {
				const list = this.form.tierList || []
				const payload = []
				for (let i = 0; i < list.length; i++) {
					const row = list[i] || {}
					const minText = String(row.min_price_yuan || '').trim()
					const maxText = String(row.max_price_yuan || '').trim()
					const marginText = String(row.margin || '').trim()
					if (!minText && !maxText && !marginText) {
						continue
					}
					const minYuan = Number(minText || 0)
					const margin = Number(marginText)
					if (isNaN(minYuan) || minYuan < 0) {
						return { error: '阶梯毛利率第 ' + (i + 1) + ' 档最低价无效' }
					}
					if (isNaN(margin) || margin < 0 || margin > 1000) {
						return { error: '阶梯毛利率第 ' + (i + 1) + ' 档毛利率需在 0-1000' }
					}
					let maxFen = null
					if (maxText !== '') {
						const maxYuan = Number(maxText)
						if (isNaN(maxYuan) || maxYuan <= minYuan) {
							return { error: '阶梯毛利率第 ' + (i + 1) + ' 档价格区间错误（min_price < max_price）' }
						}
						maxFen = Math.round(maxYuan * 100)
					}
					payload.push({
						min_price: Math.round(minYuan * 100),
						max_price: maxFen,
						margin: Math.round(margin),
					})
				}
				return { list: payload }
			},
			loadAll() {
				this.pageLoading = true
				return Promise.all([this.loadConfig(), this.loadShop()]).finally(() => {
					this.pageLoading = false
				})
			},
			loadConfig() {
				return this.$axios.get(MINI_CONFIG_API.get).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取配置失败'))
						return
					}
					this.applyConfig(res.data.data || {})
				}).catch((err) => {
					console.log(err)
					this.$message.error('获取配置失败')
				})
			},
			applyConfig(data) {
				this.configured = !!data.configured
				if (!this.configured) {
					this.hasAppsecret = false
					this.hasMchId = false
					this.hasApiKey = false
					this.hasCert = false
					this.mchIdDisplay = ''
					this.profitMargin = 200
					this.form.appsecret = ''
					this.form.mch_id = ''
					this.form.api_key = ''
					this.form.profit_margin_percent = '20'
					this.form.deduction_rate_percent = '0'
					this.form.shipping_fee_yuan = '0'
					this.form.tierList = []
					if (!this.form.min_withdraw) {
						this.form.min_withdraw = '100'
					}
					return
				}
				this.form.appid = data.appid || ''
				this.form.shop_name = data.shop_name || ''
				this.form.logo_url = data.logo_url || ''
				this.form.min_withdraw = data.min_withdraw != null && data.min_withdraw !== ''
					? String(data.min_withdraw)
					: '100'
				this.form.state = Number(data.state) === 1 ? 1 : 0
				this.form.appsecret = ''
				this.form.mch_id = ''
				this.form.api_key = ''
				this.profitMargin = Number(data.profit_margin) || 200
				this.form.profit_margin_percent = String(this.profitMargin / 10)
				this.form.deduction_rate_percent = data.deduction_rate != null && data.deduction_rate !== ''
					? String(Number(data.deduction_rate) / 10)
					: '0'
				this.form.shipping_fee_yuan = data.shipping_fee_yuan != null && data.shipping_fee_yuan !== ''
					? String(data.shipping_fee_yuan)
					: '0'
				this.form.tierList = this.mapTiersFromApi(data.profit_tiers)
				this.hasAppsecret = !!data.has_appsecret
				this.hasMchId = !!data.has_mch_id
				this.hasApiKey = !!data.has_api_key
				this.hasCert = !!data.has_cert
				this.mchIdDisplay = data.mch_id_display || ''
			},
			loadShop() {
				return this.$axios.get(MINIPROGRAM_API.list, {
					params: {
						type: SHOP_TYPE_MINIPROGRAM,
						page: 1,
						limit: 10,
					},
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.list = []
						this.shop = null
						this.$message.error(getApiErrorMessage(res, '获取店铺失败'))
						return
					}
					const payload = res.data.data || {}
					this.list = payload.list || []
					this.shop = pickMinIdMiniShop(this.list)
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.shop = null
					this.$message.error('获取店铺失败')
				})
			},
			onSave() {
				if (!canUseMiniprogram(this.appdata && this.appdata.myinfo)) {
					this.$message.warning('未开通小程序功能，请联系管理员')
					return
				}
				this.$refs.form.validate((valid) => {
					if (!valid) {
						return
					}
					const tiersResult = this.buildTiersPayload()
					if (tiersResult.error) {
						this.$message.error(tiersResult.error)
						return
					}
					const profitPercent = Number(this.form.profit_margin_percent)
					const profitMargin = isNaN(profitPercent) ? this.profitMargin : Math.round(profitPercent * 10)
					if (profitMargin < 0 || profitMargin > 1000) {
						this.$message.error('利润率需在 0-1000‰ 之间（即 0%-100%）')
						return
					}
					const deductionPercent = String(this.form.deduction_rate_percent || '').trim()
					const shippingYuan = String(this.form.shipping_fee_yuan || '').trim()
					const payload = {
						appid: String(this.form.appid || '').trim(),
						shop_name: String(this.form.shop_name || '').trim(),
						profit_margin: profitMargin,
						profit_tiers: tiersResult.list,
					}
					if (deductionPercent !== '') {
						payload.deduction_rate = Math.round(Number(deductionPercent) * 10)
						if (isNaN(payload.deduction_rate) || payload.deduction_rate < 0 || payload.deduction_rate > 1000) {
							this.$message.error('扣点率需在 0-1000‰ 之间（6=0.6%）')
							return
						}
					}
					if (shippingYuan !== '') {
						payload.shipping_fee = Math.round(Number(shippingYuan) * 100)
						if (isNaN(payload.shipping_fee) || payload.shipping_fee < 0 || payload.shipping_fee > 100000) {
							this.$message.error('运费需在 0-100000 分（0-1000 元）之间')
							return
						}
					}
					const logoUrl = String(this.form.logo_url || '').trim()
					if (logoUrl) {
						payload.logo_url = logoUrl
					}
					const appsecret = String(this.form.appsecret || '').trim()
					if (appsecret) {
						payload.appsecret = appsecret
					}
					const mchId = String(this.form.mch_id || '').trim()
					if (mchId) {
						payload.mch_id = mchId
					}
					const apiKey = String(this.form.api_key || '').trim()
					if (apiKey) {
						payload.api_key = apiKey
					}
					const minWithdraw = String(this.form.min_withdraw || '').trim()
					if (minWithdraw !== '') {
						payload.min_withdraw = minWithdraw
					}
					this.saving = true
					this.$axios.post(MINI_CONFIG_API.save, payload).then((res) => {
						if (res.data && res.data.code == 0) {
							this.$message.success('保存成功，价格约 10 分钟内生效')
							this.loadAll()
						} else {
							this.$message.error(getApiErrorMessage(res, '保存失败'))
						}
					}).catch((err) => {
						console.log(err)
						this.$message.error('保存失败，请稍后重试')
					}).finally(() => {
						this.saving = false
					})
				})
			},
			onToggle() {
				if (!this.configured || this.toggling) {
					return
				}
				this.toggling = true
				this.$axios.post(MINI_CONFIG_API.toggle, {}).then((res) => {
					if (res.data && res.data.code == 0) {
						const data = res.data.data || {}
						this.form.state = Number(data.state) === 1 ? 1 : 0
						this.$message.success(data.state_text || (this.form.state === 1 ? '已启用' : '已停用'))
					} else {
						this.$message.error(getApiErrorMessage(res, '切换失败'))
						this.loadConfig()
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error('切换失败，请稍后重试')
					this.loadConfig()
				}).finally(() => {
					this.toggling = false
				})
			},
			onCreateShop() {
				if (!canUseMiniprogram(this.appdata && this.appdata.myinfo)) {
					this.$message.warning('您暂无开通小程序店铺的权限')
					return
				}
				const payload = {}
				const shopName = String(this.form.shop_name || '').trim()
				if (shopName) {
					payload.shop_name = shopName
				}
				this.creating = true
				this.$axios.post(MINIPROGRAM_API.activate, payload).then((res) => {
					if (res.data && res.data.code == 0) {
						this.$message.success('店铺已创建')
						this.loadShop()
					} else {
						this.$message.error(getApiErrorMessage(res, '创建店铺失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error('创建店铺失败，请稍后重试')
				}).finally(() => {
					this.creating = false
				})
			},
			goShopConfig() {
				this.onsave(this.shop)
			},
			onsave(obj) {
				if (!obj || !obj.id) {
					this.$message.warning('店铺信息不完整')
					return
				}
				sessionStorage.setItem('retailShopId', String(obj.id))
				sessionStorage.setItem('storeConfigPlatform', 'miniprogram')
				this.$router.push({
					path: '/StoreConfig/miniprogram/tabsConfig',
					query: { id: obj.id },
				})
			},
			onGoods(obj) {
				if (!obj || !obj.id) {
					return
				}
				this.$router.push({
					path: '/StoreConfig/miniprogram/goods',
					query: { id: obj.id },
				})
			},
			onswitch(data) {
				if (data.state === 2) {
					this.$confirm('停用后约 10 分钟内 C 端商品池会被清空，确定停用？', '提示', {
						type: 'warning',
						confirmButtonText: '确定停用',
						cancelButtonText: '取消',
					}).then(() => {
						this.postShopState(data)
					}).catch(() => {
						this.loadShop()
					})
					return
				}
				this.postShopState(data)
			},
			postShopState(data) {
				this.$axios.post(MINIPROGRAM_API.stateToggle, {
					id: data.id,
					state: data.state,
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success(data.state === 1 ? '已启用' : '已停用')
						this.loadShop()
					} else {
						this.$message.error(getApiErrorMessage(res, '操作失败'))
						this.loadShop()
					}
				}).catch((err) => {
					console.log(err)
					this.loadShop()
				})
			},
			formatDate(time) {
				if (time == null || time === '') {
					return '-'
				}
				if (typeof time === 'number' || /^\d+$/.test(String(time))) {
					let timestamp = Number(time)
					if (String(timestamp).length === 10) {
						timestamp = timestamp * 1000
					}
					return this.$moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
				}
				return this.$moment(time).format('YYYY-MM-DD HH:mm:ss')
			},
		},
	}
</script>

<style scoped lang="less">
	.pdd {
		height: 100%;
		overflow: auto;
		padding: 16px 20px 24px;
		box-sizing: border-box;
		background-color: #fff;
	}

	.guide-card,
	.status-card {
		margin-bottom: 16px;
		padding: 16px 18px;
		border: 1px solid #ebeef5;
		border-radius: 8px;
		background: #fafbfc;
	}

	.guide-title {
		font-size: 16px;
		font-weight: 500;
		color: #303133;
		margin-bottom: 6px;
	}

	.guide-desc {
		margin: 0;
		font-size: 13px;
		line-height: 1.6;
		color: #606266;
	}

	.status-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
	}

	.status-card.is-missing {
		border-color: #f5dab1;
		background: #fdf6ec;
	}

	.status-card.is-ready {
		border-color: #c2e7b0;
		background: #f0f9eb;
	}

	.status-label {
		font-size: 12px;
		color: #909399;
		margin-bottom: 4px;
	}

	.status-name {
		font-size: 14px;
		color: #303133;
		line-height: 1.5;
	}

	.setup-form {
		max-width: 640px;
	}

	.form-section {
		margin-bottom: 8px;
		padding-bottom: 8px;
	}

	.section-title {
		font-size: 14px;
		font-weight: 500;
		color: #303133;
		margin: 8px 0 16px;
		padding-left: 8px;
		border-left: 3px solid #ff4714;
	}

	.field-tip {
		margin: 4px 0 0;
		font-size: 12px;
		line-height: 1.4;
		color: #909399;
	}

	.price-formula {
		margin: 0 0 12px 8px;
	}

	.tier-table {
		width: 100%;
	}

	.tier-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	.tier-sep {
		color: #909399;
	}

	.tier-margin {
		width: 140px;
		flex: none;
	}

	.state-text {
		margin-left: 8px;
		font-size: 13px;
		color: #606266;
	}

	.logo-upload {
		/deep/ .avatar-uploader .el-upload {
			width: 80px;
			height: 80px;
		}

		/deep/ .avatar-uploader-icon {
			width: 80px;
			height: 80px;
			line-height: 80px;
			font-size: 22px;
		}

		/deep/ .avatar {
			width: 80px;
			height: 80px;
		}
	}

	.tablebox {
		margin-bottom: 20px;
	}

	.shop-table-wrap {
		background: #fff;
		border: 1px solid #ebeef5;
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
	}

	.shop-table {
		font-size: 13px;
		color: #303133;
		background: transparent;

		&::before {
			height: 0;
		}

		/deep/ &::after {
			height: 1px;
			background-color: #ebeef5;
		}

		/deep/ thead {
			font-weight: normal;
		}

		/deep/ th.el-table__cell {
			background: #fafbfc;
			font-size: 13px;
			font-weight: normal;
			color: #606266;
			padding: 12px 0;
			border-right: none;
			border-bottom: 1px solid #ebeef5;

			.cell {
				font-size: 13px;
				font-weight: normal;
				line-height: 1.5;
			}
		}

		/deep/ td.el-table__cell {
			font-size: 13px;
			padding: 12px 0;
			border-right: none;
			border-bottom: 1px solid #ebeef5;

			.cell {
				font-size: 13px;
				line-height: 1.5;
			}
		}

		/deep/ tr:hover > td.el-table__cell {
			background: #fafbfc !important;
		}

		/deep/ .el-table__empty-text {
			font-size: 13px;
			color: #909399;
		}

		/deep/ .el-table__body-wrapper,
		/deep/ .el-table__header-wrapper {
			width: 100% !important;
		}
	}

	.shop-name {
		color: #303133;
	}

	.shop-logo {
		width: 52px;
		height: 52px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		overflow: hidden;
		background: #f5f6f8;

		img {
			max-width: 100%;
			max-height: 100%;
			object-fit: contain;
		}

		i {
			font-size: 22px;
			color: #c0c4cc;
		}
	}

	.cell-link {
		font-size: 13px;
		color: #ff4714;
		cursor: pointer;
		transition: opacity 0.2s;

		&:hover {
			opacity: 0.75;
		}
	}

	.cell-link-gap {
		margin-left: 10px;
	}

	.status-cell {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 4px 12px 4px 8px;
		border-radius: 20px;
		transition: background 0.2s;

		/deep/ .el-switch {
			height: 20px;
			line-height: 20px;

			.el-switch__core {
				width: 36px !important;
				height: 20px;
				border-radius: 10px;

				&::after {
					width: 16px;
					height: 16px;
				}
			}

			&.is-checked .el-switch__core::after {
				margin-left: -17px;
			}
		}

		.status-label {
			font-size: 13px;
			line-height: 1;
			white-space: nowrap;
		}

		&.is-on {
			background: rgba(255, 71, 20, 0.1);

			.status-label {
				color: #ff4714;
			}
		}

		&.is-off {
			background: #f5f6f8;

			.status-label {
				color: #909399;
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/miniprogram/account.vue